import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';
import { Button, Modal } from 'react-bootstrap';
import { differenceBy, isEmpty } from 'lodash';
import {
    extractFileName,
    getAllCustomer,
    popup,
    renameFileWithUUID,
    uniqueById,
} from '../../../utils';
import {
    MODAL,
    DOCUMENT_REQUEST_FORM,
    DIGITAL_LABOR_LAW_POSTER,
    QUERY_KEYS,
} from '../../../constants';
import { DocImage, FeedbackCross, IconPlus, ViewIcon } from '../../../assets/svg';
import SSCSelect from '../SSCSelect';
import {
    setFileViewModalDetails,
    setLoaderProgress,
    setLoaderVisibility,
    setShowProgress,
} from '../../../redux';
import {
    createDigitalLaborLawPoster,
    signedDigitalLaborLawPosterFile,
} from '../../../utils/apiServices/digitalLaborLawPosterAPI';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const allowedTypes = ['application/pdf'];

export const AddDigitalLaborLawPosterModal = ({ showAddModal, setShowAddModal }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [searchCustomer, setSearchCustomer] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const csmCaseload = useSelector((s) => s.csmCaseload?.csmCaseloadData);

    const [debouncedSearch] = useDebouncedValue(searchCustomer, 500);
    const {
        data: customerList,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMERS, debouncedSearch, csmCaseload?.id, csmCaseload?.role_code],
        queryFn: async () =>
            await getAllCustomer({
                offset: 1,
                limit: 50,
                search: debouncedSearch,
                id: csmCaseload?.id || '',
                role: csmCaseload?.role_code || '',
                hasPrimaryUser: true,
            }),
        select: (data) => (data.data.length > 0 ? data.data : []),
        enabled: showAddModal,
    });
    if (isError) popup('error', error.message);

    const handleRemoveFile = (indexToRemove) => {
        !!selectedFiles.length &&
            setSelectedFiles((prevSelectedFiles) =>
                prevSelectedFiles.filter((_, index) => index !== indexToRemove),
            );
    };

    const onFileChange = (event) => {
        try {
            const files = event.target.files;
            let notAllowedFiles = [];
            if (files.length > 10) {
                return popup('error', 'Maximum Ten(10) files are allowed at once.');
            }

            let selectedFilesArray = Array.from(files);

            for (const file of selectedFilesArray) {
                if (
                    !allowedTypes.includes(file.type) ||
                    file.size > DOCUMENT_REQUEST_FORM.FILE.FILE_SIZE_LIMIT_25MB
                ) {
                    notAllowedFiles.push(file);
                }
            }
            if (notAllowedFiles?.length > 0) {
                popup(
                    'error',
                    selectedFilesArray.length === 1
                        ? `File should be max "25MB"`
                        : `Some files have been removed because each file should be max "25MB"`,
                );
                selectedFilesArray = differenceBy(selectedFilesArray, notAllowedFiles, 'name');
            }
            selectedFilesArray.forEach((file) => {
                // (if file names are unique) and  Check for duplicate filenames (already uploaded)
                if (
                    selectedFiles.some(
                        (uploadedFile) =>
                            uploadedFile.name === file.name && uploadedFile.size === file.size,
                    )
                ) {
                    throw new Error(
                        `Document "${file.name}" already selected. Please choose another file.`,
                    );
                }
            });
            setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...selectedFilesArray]);
        } catch (error) {
            popup('error', error.message);
        }
    };

    // 1.) To dynamically creates the <input> element,
    // 2.) Don't need the input to persist in the DOM but only need its functionality.
    const openFileExplorer = () => {
        if (selectedFiles.length >= 10) {
            popup('error', 'Upto ten(10) files are allowed');
        } else {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = onFileChange;
            input.accept = '.pdf,.PDF';
            input.click();
        }
    };

    const handlePreview = (i) => {
        const currentFile = selectedFiles[i];
        const fileType = currentFile?.type;
        const uri = URL.createObjectURL(currentFile);
        dispatch(
            setFileViewModalDetails({
                openFileViewerModal: true,
                docs: [{ uri, fileType }],
                fileName: currentFile?.name,
            }),
        );
    };

    const readFileAsArrayBuffer = async (file) => {
        try {
            const reader = new FileReader();
            reader.readAsArrayBuffer(file);
            return new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const onUploadClick = async () => {
        const data = [];
        const filesData = [];
        const uniqueCustomers = uniqueById(selectedCustomer);
        const totalFiles = selectedFiles.length;
        let uploadedCount = 0;

        try {
            dispatch(setLoaderVisibility(true));
            dispatch(setShowProgress(true));

            /// UPLOAD FILE PARALLELS
            const uploadPromises = selectedFiles.map(async (file) => {
                const fileUUID = uuidV4();
                const signedData = {
                    file_name: renameFileWithUUID(file?.name, fileUUID),
                    actual_file_name: file?.name,
                };

                const {
                    data: { preSignedUrl, filePath },
                } = await signedDigitalLaborLawPosterFile({ signedData });

                const binaryData = await readFileAsArrayBuffer(file);
                const uploadResponse = await fetch(preSignedUrl, {
                    method: 'PUT',
                    body: binaryData,
                    headers: {
                        'Content-Type': file?.type,
                    },
                });

                if (uploadResponse.status === 200) {
                    uploadedCount += 1;
                    dispatch(setLoaderProgress(Math.round((uploadedCount / totalFiles) * 100)));

                    filesData.push({
                        file_name: extractFileName(file?.name),
                        s3_key: filePath,
                    });
                } else {
                    throw new Error(`${file.name} upload failed`);
                }
            });

            await Promise.all(uploadPromises);

            // SAVE TO DB
            for (const file of filesData) {
                for (const customer of uniqueCustomers) {
                    data.push({
                        account_id: customer?.id,
                        s3_key: file?.s3_key,
                        primary_user_id: customer?.primary_user_id,
                        file_name: file?.file_name,
                    });
                }
            }
            if (data.length > 0) {
                await createDigitalLaborLawPoster({ body: data });
                setSelectedCustomer([]);
                setSelectedFiles([]);
                setShowAddModal(false);
                queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.DIGITAL_LABOR_POSTERS],
                    exact: false,
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            dispatch(setLoaderVisibility(false));
            dispatch(setShowProgress(false));
        }
    };

    return (
        <Modal
            show={showAddModal}
            onHide={() => {
                setShowAddModal(false);
                setSelectedFiles([]);
            }}
            size='lg'
            dialogClassName='overflow-hidden'
            fullscreen='lg-down'
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title
                    id='contained-modal-title-vcenter'
                    className='d-flex align-items-center gap-2 justify-content-start w-100'
                >
                    {DIGITAL_LABOR_LAW_POSTER.PAGE_HEADING}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='d-flex flex-column gap-3 w-100 position-relative'>
                    <SSCSelect
                        required
                        name='clients'
                        label='Clients'
                        isMulti
                        placeholder='Select Client(s)...'
                        isLoading={isFetching}
                        options={customerList
                            ?.filter((c) => {
                                const ids = selectedCustomer.map((c) => c.id);
                                return !ids.includes(c.id);
                            })
                            .map((customer) => ({
                                label: customer.name,
                                value: customer,
                            }))}
                        onInputChange={setSearchCustomer}
                        onChange={(selected) => {
                            if (selected && selected.length > 0) {
                                setSelectedCustomer(selected.map((u) => u.value));
                                setSearchCustomer('');
                            } else {
                                setSelectedCustomer([]);
                            }
                        }}
                        value={
                            selectedCustomer.length > 0
                                ? selectedCustomer?.map((u) => ({
                                      label: u.name,
                                      value: u,
                                  }))
                                : null
                        }
                        onBlur={(e) => {
                            const blurValue = e.target.value;
                            if (isEmpty(blurValue)) setSearchCustomer('');
                        }}
                        formatOptionLabel={({ label, value }) => (
                            <div title={value.email || value.name}>{label}</div>
                        )}
                    />
                    <span className='d-flex gap-2 position-absolute end-0'>
                        <button
                            className='cursor-pointer text-primary border-0 bg-transparent'
                            onClick={() => setSelectedCustomer(customerList)}
                            title='Select all clients'
                            type='button'
                        >
                            Select All
                        </button>
                        <button
                            type='button'
                            className='cursor-pointer text-danger border-0 bg-transparent'
                            onClick={() => setSelectedCustomer([])}
                            disabled={selectedCustomer.length === 0}
                            title='Reset selection'
                        >
                            Reset
                        </button>
                    </span>
                    <div className='d-flex w-100 gap-2' style={{ height: '24px' }}>
                        <p className='text-sm'>
                            Selected: <b>{selectedCustomer.length}</b> client(s) and
                        </p>
                        <p className='text-sm'>
                            <b>{selectedFiles.length}</b> file(s)
                        </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-start'>
                        {selectedFiles?.length === 0 && (
                            <button
                                className='ssc-primary-green-btn upload-file-btn rounded-1'
                                style={{ fontSize: '15px', width: 'auto' }}
                                type='button'
                                onClick={openFileExplorer}
                                disabled={
                                    selectedFiles.length === DOCUMENT_REQUEST_FORM.FILE.FILE_LIMIT
                                }
                                title={'Select Files'}
                            >
                                {'Select Files'}
                            </button>
                        )}

                        {selectedFiles?.length > 0 && (
                            <div className='d-flex align-items-center file-upload mb-2'>
                                {selectedFiles?.map((file, index) => (
                                    <div
                                        key={index}
                                        className='d-flex align-items-start flex-column position-relative'
                                    >
                                        <span title={file.name}>
                                            <DocImage />
                                        </span>
                                        {file.name.length < 10 ? (
                                            <span
                                                title={file.name}
                                                className='mt-2'
                                                style={{ fontSize: '13px' }}
                                            >
                                                {file.name}
                                            </span>
                                        ) : (
                                            <span
                                                className='truncate-text mt-2'
                                                title={file.name}
                                                style={{ fontSize: '13px' }}
                                            >
                                                {file.name.slice(0, 6) +
                                                    '...' +
                                                    file.name.split('.')[1]}
                                            </span>
                                        )}
                                        <button
                                            id='action-btn'
                                            className='remove-file-btn position-absolute'
                                            onClick={() => handleRemoveFile(index)}
                                        >
                                            <FeedbackCross />
                                        </button>
                                        <span
                                            className='position-absolute'
                                            style={{
                                                bottom: '20px',
                                                right: '-5px',
                                                background: 'white',
                                            }}
                                            id='action-btn'
                                            onClick={() => handlePreview(index)}
                                            title='View'
                                        >
                                            <ViewIcon />
                                        </span>
                                    </div>
                                ))}
                                {selectedFiles?.length > 0 && (
                                    <span className='ms-3'>
                                        <button
                                            id='action-btn-green'
                                            type='button'
                                            onClick={openFileExplorer}
                                        >
                                            <IconPlus />
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='d-flex gap-3 w-100 align-items-center justify-content-end modal-btn mt-3'>
                    <Button
                        type='button'
                        className='ssc-secondary-white-btn '
                        onClick={() => {
                            setShowAddModal(false);
                        }}
                        style={{ height: '40px', fontSize: '15px' }}
                        title={MODAL.CANCEL_BUTTON}
                    >
                        {MODAL.CANCEL_BUTTON}
                    </Button>
                    <span
                        title={
                            selectedFiles.length === 0 || selectedCustomer.length === 0
                                ? 'Select at least one file and one client'
                                : 'Upload'
                        }
                    >
                        <Button
                            type='submit'
                            onClick={onUploadClick}
                            className='table-footer-btn'
                            disabled={selectedFiles.length === 0 || selectedCustomer.length === 0}
                            style={{ height: '40px' }}
                        >
                            {'Upload'}
                        </Button>
                    </span>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

AddDigitalLaborLawPosterModal.propTypes = {
    showAddModal: PropTypes.bool.isRequired,
    setShowAddModal: PropTypes.func.isRequired,
    debouncedApiCall: PropTypes.func,
};
